import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// ===== helpers =====

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function toRawAnswer(userAnswer) {
  if (userAnswer == null) return "";
  if (typeof userAnswer === "string" || typeof userAnswer === "number") {
    return String(userAnswer);
  }
  if (typeof userAnswer === "object") {
    return String(
      userAnswer.raw ??
        userAnswer.value ??
        userAnswer.text ??
        userAnswer.input ??
        userAnswer.latex ??
        ""
    );
  }
  return String(userAnswer);
}

function evaluateExpression(expr) {
  const cleaned = String(expr || "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\\\sqrt\{(\d+)\}/g, "√$1")
    .replace(/\\sqrt\{(\d+)\}/g, "√$1")
    .replace(/sqrt\{(\d+)\}/g, "√$1")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1");

  try {
    const parsed = cleaned
      .replace(/(\d*)√(\d+)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+)/g, (_, b) => `Math.sqrt(${b})`);

    const parsed2 = parsed.replace(
      /\\sqrt\{(\d+)\}/g,
      (_, b) => `Math.sqrt(${b})`
    );
    return Function(`return ${parsed2}`)();
  } catch {
    return NaN;
  }
}

function normalizeCanonical(str) {
  return String(str || "")
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\\√/g, "√")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .replace(/^1√/, "√")
    .replace(/√0*(\d+)/g, (_, d) => `√${parseInt(d, 10)}`)
    .replace(/^0*(\d+)$/, (_, d) => `${parseInt(d, 10)}`)
    .trim();
}

function simplifySqrt(n) {
  let factor = 1;
  for (let i = Math.floor(Math.sqrt(n)); i >= 2; i--) {
    if (n % (i * i) === 0) {
      factor = i;
      break;
    }
  }
  const outside = factor;
  const inside = n / (factor * factor);
  if (inside === 1) return `${outside}`;
  if (outside === 1) return `\\sqrt{${inside}}`;
  return `${outside}\\sqrt{${inside}}`;
}

// parsuje latex pierwiastka w formie:
// - \sqrt{m}
// - k\sqrt{m}
function parseRootLatex(rootLatex) {
  const s = String(rootLatex || "").replace(/\s/g, "");

  // k\sqrt{m}
  let m = s.match(/^(\d+)\\sqrt\{(\d+)\}$/);
  if (m) return { coeff: parseInt(m[1], 10), inside: parseInt(m[2], 10) };

  // \sqrt{m}
  m = s.match(/^\\sqrt\{(\d+)\}$/);
  if (m) return { coeff: 1, inside: parseInt(m[1], 10) };

  // fallback
  return { coeff: 1, inside: NaN };
}

function formatRootLatex(coeff, inside) {
  if (!Number.isFinite(inside)) return "";
  if (coeff === 1) return `\\sqrt{${inside}}`;
  return `${coeff}\\sqrt{${inside}}`;
}

function mulRootLatex(rootLatex, k) {
  const { coeff, inside } = parseRootLatex(rootLatex);
  return formatRootLatex(coeff * k, inside);
}

// ===== generator =====

export function generateTask(modeOrOptions = "minus") {
  const mode =
    typeof modeOrOptions === "string"
      ? modeOrOptions
      : modeOrOptions?.mode || "minus";
  const nPool = [
    2, 3, 5, 6, 7, 10, 11, 13, 14, 15, 17, 19, 21, 22, 23, 26, 29, 30, 31,
  ];

  const n = pick(nPool);
  const rootSimpl = simplifySqrt(n);

  const rootVal = Math.sqrt(n);
  const f = Math.floor(rootVal);

  const low = f;
  const high = f + 1;
  const [a, b] = Math.random() < 0.5 ? [low, high] : [high, low];

  const sign = mode === "plus" ? "+" : "-";
  const exprLatex = `\\left|${rootSimpl} - ${a}\\right| ${sign} \\left|${rootSimpl} - ${b}\\right|`;

  const termAIsNeg = rootVal - a < 0;
  const termBIsNeg = rootVal - b < 0;

  const absA = termAIsNeg ? `${a} - ${rootSimpl}` : `${rootSimpl} - ${a}`;
  const absB = termBIsNeg ? `${b} - ${rootSimpl}` : `${rootSimpl} - ${b}`;

  const resultLatex =
    mode === "plus" ? `(${absA}) + (${absB})` : `(${absA}) - (${absB})`;

  const between = rootVal > low && rootVal < high;
  const right = rootVal > high;

  let simplifiedResult;

  if (between && mode === "plus") simplifiedResult = `${high - low}`;
  else if (right && mode === "plus")
    simplifiedResult = `${mulRootLatex(rootSimpl, 2)} - ${a + b}`;
  else if (between && mode === "minus" && a === high)
    simplifiedResult = `${a + b} - ${mulRootLatex(rootSimpl, 2)}`;
  else if (between && mode === "minus")
    simplifiedResult = `${mulRootLatex(rootSimpl, 2)} - ${a + b}`;
  else if (right && mode === "minus") simplifiedResult = `${b - a}`;
  else simplifiedResult = resultLatex;

  // ✅ "Sprowadzamy pierwiastek..." pokazujemy tylko gdy ma sens (gdy coeff > 1)
  const { coeff, inside } = parseRootLatex(rootSimpl);

  // Przykład sensowny: 3√3 = √9·√3 = √27
  // Przykład bez sensu: √7 = √7  -> pomijamy
  const sqrtComparableLatex =
    coeff > 1 && Number.isFinite(inside)
      ? `${rootSimpl} = \\sqrt{${coeff * coeff}}\\cdot\\sqrt{${inside}} = \\sqrt{${n}}`
      : "";

  return {
    type: "AbsSqrtAbsPairOpenTask",
    data: {
      mode,
      n,
      a,
      b,
      rootSimpl,
      exprLatex,
      simplifiedResult,
      between,
      right,
      termAIsNeg,
      termBIsNeg,
      absA,
      absB,
      resultLatex,
      sqrtComparableLatex,
    },
    tags: [
      "wartość_bezwzględna",
      "pierwiastki",
      mode === "plus" ? "suma" : "różnica",
    ],
  };
}

// ===== rendering =====

export function renderTask({ data }) {
  const { exprLatex } = data;
  return (
    <div
      className="space-y-3"
      style={{ textAlign: "left", paddingBottom: 18, marginBottom: 10 }}
    >
      <p style={{ marginBottom: 0 }}>
        Oblicz wartość wyrażenia:
        <span style={{ marginLeft: 14 }}>
          <InlineMath math={exprLatex} />
        </span>
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <SqrtInput
      value={toRawAnswer(userAnswer)}
      onChange={onChange}
      disabled={disabled}
      wide={true}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = toRawAnswer(userAnswer);
  if (!raw || String(raw).trim() === "") return false;

  const expectedNorm = normalizeCanonical(data.simplifiedResult);
  const givenNorm = normalizeCanonical(raw);

  const expectedVal = evaluateExpression(expectedNorm);
  const givenVal = evaluateExpression(givenNorm);

  if (!Number.isFinite(expectedVal) || !Number.isFinite(givenVal)) return false;
  return Math.abs(expectedVal - givenVal) < 0.01;
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.simplifiedResult} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = toRawAnswer(userAnswer);
  if (!raw || String(raw).trim() === "")
    return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={String(raw)} />;
}

export function renderExplanation({ data }) {
  const {
    n,
    a,
    b,
    rootSimpl,
    mode,
    between,
    right,
    absA,
    absB,
    resultLatex,
    simplifiedResult,
    sqrtComparableLatex,
  } = data;

  const sign = mode === "plus" ? "+" : "-";

  const a2 = a * a;
  const b2 = b * b;

  let compareLatex;
  if (right)
    compareLatex = `${b} < ${a} < \\sqrt{${n}} \\quad\\Longleftrightarrow\\quad ${b2} < ${a2} < ${n}`;
  else if (between)
    compareLatex = `${b} < \\sqrt{${n}} < ${a} \\quad\\Longleftrightarrow\\quad ${b2} < ${n} < ${a2}`;
  else
    compareLatex = `\\sqrt{${n}} < ${b} < ${a} \\quad\\Longleftrightarrow\\quad ${n} < ${b2} < ${a2}`;

  const step1 = `\\left|${rootSimpl} - ${a}\\right| = ${absA}`;
  const step2 = `\\left|${rootSimpl} - ${b}\\right| = ${absB}`;

  const finalSubstitution = `\\left|${rootSimpl} - ${a}\\right| ${sign} \\left|${rootSimpl} - ${b}\\right| = ${resultLatex}`;
  const finalResult = `= ${simplifiedResult}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong>{" "}
        {sqrtComparableLatex
          ? "Sprowadzamy pierwiastek do postaci ułatwiającej porównanie:"
          : "Porównujemy względem liczb, korzystając z kwadratów:"}

        {/* ✅ tylko jeśli ma sens */}
        {sqrtComparableLatex ? (
          <div style={{ textAlign: "center" }}>
            <BlockMath math={sqrtComparableLatex} />
          </div>
        ) : null}

        <div>
          Następnie porównujemy względem liczb <InlineMath math={`${b}`} /> i{" "}
          <InlineMath math={`${a}`} /> (porównujemy kwadraty):
          <div style={{ textAlign: "center" }}>
            <BlockMath math={compareLatex} />
          </div>
        </div>
      </div>

      <div>
        <strong>Krok 2.</strong> Zdejmujemy wartości bezwzględne:
        <div style={{ textAlign: "center" }}>
          <BlockMath math={step1} />
          <BlockMath math={step2} />
        </div>
      </div>

      <div>
        <strong>Krok 3.</strong>{" "}
        {mode === "plus" ? "Dodajemy" : "Odejmujemy"} otrzymane wyrażenia:
        <div style={{ textAlign: "center" }}>
          <BlockMath math={finalSubstitution} />
          <BlockMath math={finalResult} />
        </div>
      </div>
    </div>
  );
}
